import type { Block, BlockType } from '@emailforge/schemas';
import { Button } from '@emailforge/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import { useListApprovalsQuery } from '@/features/collab/approvalsApi';
import {
  addBlock,
  addBlockToColumn,
  loadTemplate,
  moveBlock,
  moveColumnChild,
  redo,
  removeBlock,
  removeColumnChild,
  saveCompleted,
  saveStarted,
  selectBlock,
  setColorScheme,
  setViewport,
  undo,
} from '@/features/editor/editorSlice';
import {
  useGetTemplateQuery,
  useHeartbeatTemplateMutation,
  usePublishVersionMutation,
  useSaveVersionMutation,
} from '@/features/templates/templatesApi';
import { t } from '@/lib/i18n';
import { useShortcut } from '@/lib/keyboard';
import { ApprovalsPanel } from '@/screens/app/editor/ApprovalsPanel';
import { createBlock } from '@/screens/app/editor/blockDefaults';
import { BlockLibrary } from '@/screens/app/editor/BlockLibrary';
import { BlockPreview, type ColumnEditorBindings } from '@/screens/app/editor/BlockPreview';
import { CommentsPanel } from '@/screens/app/editor/CommentsPanel';
import { Inspector } from '@/screens/app/editor/Inspector';
import { PreflightModal } from '@/screens/app/editor/PreflightModal';
import { SnippetsPanel } from '@/screens/app/editor/SnippetsPanel';
import { TrackingDefaultsControl } from '@/screens/app/editor/TrackingDefaultsControl';
import { VersionHistoryDrawer } from '@/screens/app/editor/VersionHistoryDrawer';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

export function EditorScreen() {
  const { templateId = '' } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data, isLoading, error } = useGetTemplateQuery(templateId, { skip: !templateId });
  const { data: approvals = [] } = useListApprovalsQuery(data?.latestVersion?.id ?? '', {
    skip: !data?.latestVersion?.id,
  });
  const pendingApproval = approvals.find((a) => a.status === 'pending');
  const [saveVersion, { isLoading: isSaving }] = useSaveVersionMutation();
  const [publishVersion, { isLoading: isPublishing }] = usePublishVersionMutation();
  const [heartbeat] = useHeartbeatTemplateMutation();

  const doc = useAppSelector((s) => s.editor.document);
  const selectedBlockId = useAppSelector((s) => s.editor.selectedBlockId);
  const isDirty = useAppSelector((s) => s.editor.isDirty);
  const viewport = useAppSelector((s) => s.editor.viewport);
  const colorScheme = useAppSelector((s) => s.editor.colorScheme);
  const lastSavedAt = useAppSelector((s) => s.editor.lastSavedAt);
  const canUndo = useAppSelector((s) => s.editor.past.length > 0);
  const canRedo = useAppSelector((s) => s.editor.future.length > 0);
  const currentUserId = useAppSelector((s) => s.auth.user?.id ?? '');
  const [rightTab, setRightTab] = useState<'inspector' | 'comments' | 'approvals'>('inspector');
  const [leftPanelTab, setLeftPanelTab] = useState<'blocks' | 'snippets'>('blocks');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [preflightOpen, setPreflightOpen] = useState(false);

  useEffect(() => {
    if (!data?.latestVersion) return;
    dispatch(
      loadTemplate({
        templateId: data.id,
        versionId: data.latestVersion.id,
        document: data.latestVersion.astJson,
      }),
    );
  }, [data?.id, data?.latestVersion?.id, data?.latestVersion, dispatch]);

  useEffect(() => {
    if (!templateId) return;
    heartbeat(templateId);
    const interval = window.setInterval(() => {
      heartbeat(templateId);
    }, 30_000);
    return () => window.clearInterval(interval);
  }, [templateId, heartbeat]);

  const timeoutRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isDirty || !templateId || !data?.latestVersion) return;
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(async () => {
      dispatch(saveStarted());
      try {
        await saveVersion({
          templateId,
          astJson: doc,
          variablesSchema: data.latestVersion?.variablesSchema ?? { version: 1, fields: [], sampleData: {} },
        }).unwrap();
        dispatch(saveCompleted());
      } catch (err) {
         
        console.error('Save failed', err);
      }
    }, 1200);
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [doc, isDirty, templateId, saveVersion, dispatch, data?.latestVersion]);

  const selected = useMemo(() => doc.blocks.find((b) => b.id === selectedBlockId) ?? null, [doc, selectedBlockId]);

  // Force-save now (instead of waiting on the 1.2s autosave debounce). Useful
  // before closing a tab or switching templates. Allowed inside inputs because
  // saving while typing is the expected behavior.
  useShortcut(
    'mod+s',
    async () => {
      if (!isDirty || !data?.latestVersion) return;
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      dispatch(saveStarted());
      try {
        await saveVersion({
          templateId,
          astJson: doc,
          variablesSchema: data.latestVersion.variablesSchema,
        }).unwrap();
        dispatch(saveCompleted());
      } catch (err) {
         
        console.error('Save failed', err);
      }
    },
    { allowInInput: true, deps: [doc, isDirty, templateId, data?.latestVersion?.id] },
  );
  useShortcut('mod+z', () => dispatch(undo()));
  useShortcut('mod+shift+z', () => dispatch(redo()));

  if (!templateId) {
    return (
      <>
        <TopBar crumbs={[t('nav.templates'), t('crumbs.editor')]} />
        <div style={{ padding: 32, color: 'var(--ink-3)' }}>No template selected.</div>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <TopBar crumbs={[t('nav.templates'), 'Loading…']} />
        <LoadingState />
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <TopBar crumbs={[t('nav.templates'), 'Error']} />
        <div style={{ padding: 32, color: 'var(--err)' }}>
          Couldn't load this template.{' '}
          <a onClick={() => navigate('/app/templates')} style={{ cursor: 'pointer', color: 'var(--flame-2)' }}>
            Back to templates
          </a>
          .
        </div>
      </>
    );
  }

  const saveStatus = isSaving ? 'Saving…' : isDirty ? 'Unsaved' : lastSavedAt ? 'Saved' : 'Up to date';
  const statusColor = isSaving ? 'var(--ink-3)' : isDirty ? 'var(--warn)' : 'var(--spark)';

  return (
    <>
      <TopBar
        crumbs={[t('nav.templates'), data.name]}
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="mono" style={{ fontSize: 11, color: statusColor }}>
              ● {saveStatus}
            </span>
            <div className="mf-vdiv" style={{ height: 22 }} />
            <Button
              variant="ghost"
              size="sm"
              icon
              disabled={!canUndo}
              onClick={() => dispatch(undo())}
              title="Undo (⌘Z)"
              aria-label="Undo"
            >
              <Icon.undo size={13} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon
              disabled={!canRedo}
              onClick={() => dispatch(redo())}
              title="Redo (⌘⇧Z)"
              aria-label="Redo"
            >
              <Icon.redo size={13} />
            </Button>
            <div className="mf-vdiv" style={{ height: 22 }} />
            <Button variant="ghost" size="sm" onClick={() => navigate(`/app/templates/${templateId}/variables`)}>
              <Icon.variable size={13} /> Variables
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/app/templates/${templateId}/preview`)}>
              <Icon.eye size={13} /> Preview
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/app/templates/${templateId}/code`)}>
              <Icon.code size={13} /> Code
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(true)}>
              <Icon.history size={13} /> History
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setPreflightOpen(true)}>
              <Icon.zap size={13} /> Preflight
            </Button>
            <TrackingDefaultsControl
              templateId={templateId}
              current={data.defaultTracking ?? null}
            />
            <Button variant="flame" size="sm" disabled={!data.latestVersion || isPublishing || isDirty || Boolean(pendingApproval)}
              title={pendingApproval ? 'Approval pending — resolve before publishing' : undefined}
              onClick={async () => {
                if (!data.latestVersion) return;
                await publishVersion({ templateId, versionId: data.latestVersion.id }).unwrap();
              }}
            >
              {isPublishing
                ? 'Publishing…'
                : pendingApproval
                  ? 'Awaiting approval'
                  : data.status === 'published'
                    ? 'Publish update'
                    : 'Publish'}
            </Button>
          </div>
        }
      />

      {data.lock && data.lock.byUserId !== currentUserId && (
        <div
          style={{
            padding: '8px 16px',
            background: 'var(--flame-soft)',
            borderBottom: '1px solid var(--flame-line)',
            color: 'var(--flame-2)',
            fontSize: 12.5,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Icon.bolt size={13} />
          <span>
            <strong>{data.lock.byUserName ?? data.lock.byUserEmail ?? 'Another teammate'}</strong> is also editing this
            template right now. Concurrent edits aren't merged — last save wins.
          </span>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '240px 1fr 320px',
          height: 'calc(100vh - 44px)',
          background: 'var(--paper-2)',
        }}
      >
        <aside
          style={{
            borderRight: '1px solid var(--line)',
            background: 'var(--paper)',
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              borderBottom: '1px solid var(--line-soft)',
              display: 'flex',
              padding: '0 8px',
            }}
          >
            {(['blocks', 'snippets'] as const).map((tab) => {
              const active = leftPanelTab === tab;
              return (
                <button
                  key={tab}
                  onClick={() => setLeftPanelTab(tab)}
                  className="mono"
                  style={{
                    flex: 1,
                    padding: '12px 8px',
                    fontSize: 10.5,
                    textTransform: 'uppercase',
                    letterSpacing: 0.08,
                    color: active ? 'var(--ink)' : 'var(--ink-3)',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `2px solid ${active ? 'var(--flame)' : 'transparent'}`,
                    cursor: 'pointer',
                    fontWeight: active ? 600 : 500,
                  }}
                >
                  {tab}
                </button>
              );
            })}
          </div>
          {leftPanelTab === 'blocks' ? <BlockLibrary /> : <SnippetsPanel />}
        </aside>

        <div style={{ position: 'relative', overflowY: 'auto', padding: '20px 0' }}>
          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              marginBottom: 20,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <div className="mf-card" style={{ display: 'flex', padding: 2, gap: 2, background: 'var(--paper)' }}>
              <ViewportBtn active={viewport === 'desktop'} onClick={() => dispatch(setViewport('desktop'))}>
                <Icon.monitor size={13} />
              </ViewportBtn>
              <ViewportBtn active={viewport === 'mobile'} onClick={() => dispatch(setViewport('mobile'))}>
                <Icon.phone size={13} />
              </ViewportBtn>
              <div style={{ width: 1, background: 'var(--line)' }} />
              <ViewportBtn active={colorScheme === 'light'} onClick={() => dispatch(setColorScheme('light'))}>
                <Icon.sun size={13} />
              </ViewportBtn>
              <ViewportBtn active={colorScheme === 'dark'} onClick={() => dispatch(setColorScheme('dark'))}>
                <Icon.moon size={13} />
              </ViewportBtn>
            </div>
          </div>

          <Canvas
            viewport={viewport}
            colorScheme={colorScheme}
            blocks={doc.blocks}
            selectedId={selectedBlockId}
            onSelect={(id) => dispatch(selectBlock(id))}
            onDrop={(type, index) => dispatch(addBlock({ block: createBlock(type), atIndex: index }))}
            onMove={(id, toIndex) => dispatch(moveBlock({ blockId: id, toIndex }))}
            onRemove={(id) => dispatch(removeBlock(id))}
            columnEditor={{
              selectedId: selectedBlockId,
              onSelectChild: (id) => dispatch(selectBlock(id)),
              onDropNewIntoColumn: ({ columnsBlockId, columnIndex, atIndex, type }) =>
                dispatch(
                  addBlockToColumn({
                    block: createBlock(type),
                    columnsBlockId,
                    columnIndex,
                    atIndex,
                  }),
                ),
              onMoveColumnChild: (params) => dispatch(moveColumnChild(params)),
              onRemoveColumnChild: (columnsBlockId, blockId) =>
                dispatch(removeColumnChild({ columnsBlockId, blockId })),
            }}
          />
        </div>

        <aside
          style={{
            borderLeft: '1px solid var(--line)',
            background: 'var(--paper)',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 0,
              borderBottom: '1px solid var(--line-soft)',
              padding: '0 14px',
            }}
          >
            {(['inspector', 'comments', 'approvals'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setRightTab(t)}
                style={{
                  padding: '12px 14px 10px',
                  fontSize: 12,
                  fontWeight: rightTab === t ? 600 : 500,
                  color: rightTab === t ? 'var(--ink)' : 'var(--ink-3)',
                  border: 'none',
                  background: 'transparent',
                  borderBottom: `2px solid ${rightTab === t ? 'var(--flame)' : 'transparent'}`,
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {t}
                {t === 'approvals' && pendingApproval && (
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: 'var(--flame)',
                    }}
                  />
                )}
              </button>
            ))}
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {rightTab === 'inspector' && <Inspector block={selected} />}
            {rightTab === 'comments' && data.latestVersion && (
              <CommentsPanel
                versionId={data.latestVersion.id}
                selectedBlockId={selectedBlockId}
                currentUserId={currentUserId}
              />
            )}
            {rightTab === 'approvals' && data.latestVersion && (
              <ApprovalsPanel versionId={data.latestVersion.id} />
            )}
          </div>
        </aside>
      </div>
      <VersionHistoryDrawer
        templateId={templateId}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
      <PreflightModal
        templateId={templateId}
        open={preflightOpen}
        onClose={() => setPreflightOpen(false)}
      />
    </>
  );
}

function ViewportBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button size="sm" icon onClick={onClick}
      style={{
        background: active ? 'var(--paper-3)' : 'transparent',
        border: 'none',
        boxShadow: 'none',
      }}>
      {children}
    </Button>
  );
}

function Canvas({
  viewport,
  colorScheme,
  blocks,
  selectedId,
  onSelect,
  onDrop,
  onMove,
  onRemove,
  columnEditor,
}: {
  viewport: 'desktop' | 'mobile';
  colorScheme: 'light' | 'dark';
  blocks: Block[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onDrop: (type: BlockType, index: number) => void;
  onMove: (id: string, toIndex: number) => void;
  onRemove: (id: string) => void;
  columnEditor: ColumnEditorBindings;
}) {
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const width = viewport === 'desktop' ? 600 : 360;
  const bg = colorScheme === 'dark' ? 'oklch(0.19 0.005 85)' : 'white';

  return (
    <div
      style={{
        width,
        margin: '0 auto',
        background: bg,
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-3)',
        boxShadow: 'var(--sh-md)',
        minHeight: 400,
        padding: 24,
        transition: 'width 200ms ease',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onSelect(null);
      }}
    >
      {blocks.length === 0 && <EmptyCanvas />}
      {blocks.map((b, i) => (
        <div key={b.id}>
          <DropZone
            active={dropIndex === i}
            onDragEnter={() => setDropIndex(i)}
            onDragLeave={() => setDropIndex(null)}
            onDrop={(type) => {
              onDrop(type, i);
              setDropIndex(null);
            }}
          />
          <BlockFrame
            selected={selectedId === b.id}
            onSelect={() => onSelect(b.id)}
            onMoveUp={i > 0 ? () => onMove(b.id, i - 1) : undefined}
            onMoveDown={i < blocks.length - 1 ? () => onMove(b.id, i + 1) : undefined}
            onRemove={() => onRemove(b.id)}
          >
            <BlockPreview block={b} editor={columnEditor} />
          </BlockFrame>
        </div>
      ))}
      <DropZone
        active={dropIndex === blocks.length}
        onDragEnter={() => setDropIndex(blocks.length)}
        onDragLeave={() => setDropIndex(null)}
        onDrop={(type) => {
          onDrop(type, blocks.length);
          setDropIndex(null);
        }}
      />
    </div>
  );
}

function EmptyCanvas() {
  return (
    <div
      style={{
        minHeight: 260,
        border: '1.5px dashed var(--line)',
        borderRadius: 'var(--r-3)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        color: 'var(--ink-3)',
      }}
    >
      <Icon.layers size={20} />
      <div className="mono" style={{ fontSize: 11, letterSpacing: 0.06, textTransform: 'uppercase' }}>
        Empty canvas
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Drag a block from the left panel — or click to append.</div>
    </div>
  );
}

function DropZone({
  active,
  onDragEnter,
  onDragLeave,
  onDrop,
}: {
  active: boolean;
  onDragEnter: () => void;
  onDragLeave: () => void;
  onDrop: (type: BlockType) => void;
}) {
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        e.preventDefault();
        const type = e.dataTransfer.getData('application/x-block-type') as BlockType;
        if (type) onDrop(type);
      }}
      style={{
        height: active ? 24 : 6,
        margin: active ? '4px 0' : 0,
        transition: 'height 120ms ease',
        background: active ? 'var(--flame-soft)' : 'transparent',
        border: active ? '1px dashed var(--flame-line)' : undefined,
        borderRadius: 'var(--r-2)',
      }}
    />
  );
}

function BlockFrame({
  selected,
  onSelect,
  onMoveUp,
  onMoveDown,
  onRemove,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onRemove: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      style={{
        position: 'relative',
        border: `1px solid ${selected ? 'var(--flame)' : 'transparent'}`,
        borderRadius: 'var(--r-2)',
        boxShadow: selected ? 'var(--sh-focus)' : undefined,
      }}
    >
      {selected && (
        <div
          style={{
            position: 'absolute',
            top: -28,
            right: 0,
            display: 'flex',
            gap: 4,
            zIndex: 20,
          }}
        >
          {onMoveUp && (
            <Button size="sm" icon onClick={(e) => {
                e.stopPropagation();
                onMoveUp();
              }}
            >
              <Icon.arrowUp size={12} />
            </Button>
          )}
          {onMoveDown && (
            <Button size="sm" icon onClick={(e) => {
                e.stopPropagation();
                onMoveDown();
              }}
            >
              <Icon.arrowDown size={12} />
            </Button>
          )}
          <Button size="sm" icon onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <Icon.trash size={12} />
          </Button>
        </div>
      )}
      {children}
    </div>
  );
}

function LoadingState() {
  return (
    <div style={{ padding: 60, textAlign: 'center' }}>
      <div
        className="mono"
        style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.06 }}
      >
        Loading template…
      </div>
    </div>
  );
}
